import type { CaptureResult } from "posthog-js";

const IOS_WEBKIT_BRIDGE_TYPE_ERROR =
  /^(?:undefined|null) is not an object \(evaluating (['"])window\.webkit\.messageHandlers(?:\.[^'"]*)?\1\)$/;

// Firefox throws a cross-origin security error when posthog-js's
// exception-autocapture extension reads the `__POSTHOG_INSTRUMENTED__` marker
// off a handler stamped on a cross-origin frame. It surfaces as an "Error"
// with this exact message and does not represent any real app failure.
const FIREFOX_CROSS_ORIGIN_INSTRUMENTED_ERROR =
  /^Permission denied to access property (['"])__POSTHOG_INSTRUMENTED__\1$/;

// Facebook and Instagram inject `sendDataToNative` into their Android
// WebViews. When the WebView detaches, that injected code can call its native
// bridge after the Java object is gone. These exact messages only occur in
// Meta's in-app browsers and do not represent failures in our application.
const META_ANDROID_WEBVIEW_BRIDGE_ERROR =
  /^Error invoking postMessage: Java (?:object is gone|exception was raised during method invocation|bridge method invocation error)$/;

// An automated email link scanner opens our email links in headless Chrome and
// rejects promises with this non-Error value. posthog-js wraps it in the
// non-Error prefix. The Id number changes per event, which splits one noise
// source into many issues. Only the observed update/4 shape is filtered.
const EMAIL_LINK_SCANNER_REJECTION =
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4$/;

export function filterPostHogBrowserEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event || event.event !== "$exception") {
    return event;
  }

  const exceptionList = event.properties.$exception_list;
  if (!Array.isArray(exceptionList) || exceptionList.length !== 1) {
    return event;
  }

  const [exception] = exceptionList;
  if (!exception || typeof exception !== "object") {
    return event;
  }

  const { type, value } = exception as Record<string, unknown>;
  if (typeof value !== "string") {
    return event;
  }

  if (type === "TypeError" && IOS_WEBKIT_BRIDGE_TYPE_ERROR.test(value)) {
    return null;
  }

  if (type === "Error" && FIREFOX_CROSS_ORIGIN_INSTRUMENTED_ERROR.test(value)) {
    return null;
  }

  if (type === "Error" && META_ANDROID_WEBVIEW_BRIDGE_ERROR.test(value)) {
    return null;
  }

  if (
    type === "UnhandledRejection" &&
    EMAIL_LINK_SCANNER_REJECTION.test(value)
  ) {
    return null;
  }

  return event;
}
